import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Player {
  bowlerId: number;
  playerId: number;
  bowlerName: string;
  playerName: string;
  email: string;
  city: string;
  country: string;
  average: number;
  handicap: number;
  leagues: Array<{
    leagueId: number;
    leagueName: string;
    venue: string;
    city: string;
  }>;
}

interface Package {
  id: number;
  package_id: number;
  name: string;
  description: string;
  price: number;
  includes: string;
}

interface Squad {
  id: number;
  name: string;
  date: string;
  start_time: string;
  end_time: string;
  games_per_squad: number;
}

interface PlayerDetails {
  id: number;
  name: string;
  email: string;
  city: string;
  country: string;
  bowlerId: number;
  bowlerName: string;
  average: number;
  handicap: number;
  registrationStatus?: string;
}

interface CheckInComponentProps {
  leagueId: number;
  onBack: () => void;
}

const CheckInComponent: React.FC<CheckInComponentProps> = ({ leagueId, onBack }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Selected player and checkout state
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerDetails | null>(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState<number>(leagueId);
  const [leagueName, setLeagueName] = useState<string>('');
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<Package[]>([]);
  const [paidPackages, setPaidPackages] = useState<Package[]>([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState<{
    amount: number;
    paymentMethod: string;
    packages: Package[];
    registrationStatus: string;
    timestamp: string;
  } | null>(null);

  // Add Package Modal state
  const [showAddPackageModal, setShowAddPackageModal] = useState(false);
  const [availableSquads, setAvailableSquads] = useState<Squad[]>([]);
  const [newPackage, setNewPackage] = useState<{
    name: string;
    description: string;
    price: number;
    includes: number[];
  }>({
    name: '',
    description: '',
    price: 0,
    includes: []
  });
  const [packageLoading, setPackageLoading] = useState(false);
  const [packageError, setPackageError] = useState('');

  // Pre-loaded bowler list (paginated)
  interface BowlerListEntry {
    bowlerId: number;
    playerId: number;
    bowlerName: string;
    playerName: string;
    city: string;
    country: string;
    average: number;
    handicap: number;
    registrationStatus: string;
  }
  const [allBowlers, setAllBowlers] = useState<BowlerListEntry[]>([]);
  const [bowlerListPage, setBowlerListPage] = useState(0);
  const BOWLERS_PER_PAGE = 10;

  useEffect(() => {
    const fetchAllBowlers = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const res = await fetch(`${apiUrl}/checkin/league/${leagueId}/bowlers`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAllBowlers(data.bowlers || []);
        }
      } catch {
        // silent — search still works
      }
    };
    fetchAllBowlers();
  }, [leagueId]);

  // Calculate total
  const selectedTotal = selectedPackages.reduce((sum, pkg) => sum + parseFloat(pkg.price.toString()), 0);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError(t('checkIn.errors.enterPlayerInfo'));
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/checkin/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.players || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('checkIn.errors.searchFailed'));
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(t('checkIn.errors.searchFailed'));
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerDetails = useCallback(async (playerId: number, leagueId: number) => {
    setCheckoutLoading(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Fetch player details
      const response = await fetch(`${apiUrl}/checkin/player/${playerId}/league/${leagueId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPlayer(data.player);
        setAvailablePackages(data.availablePackages || []);
        setSelectedPackages(data.selectedPackages || []);
        setPaidPackages(data.paidPackages || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('checkIn.errors.loadDetailsFailed'));
      }

      // Fetch league details
      const leagueResponse = await fetch(`${apiUrl}/leagues/${leagueId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (leagueResponse.ok) {
        const leagueData = await leagueResponse.json();
        setLeagueName(leagueData.name || 'Unknown League');
      }
    } catch (error) {
      console.error('Fetch details error:', error);
      setError(t('checkIn.errors.loadDetailsFailed2'));
    } finally {
      setCheckoutLoading(false);
    }
  }, [t]);

  const handlePlayerSelect = (playerId: number, selectedLeagueId: number) => {
    setSelectedLeagueId(selectedLeagueId);
    fetchPlayerDetails(playerId, selectedLeagueId);
  };

  const handleNewSearch = () => {
    setSelectedPlayer(null);
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
    setError('');
    setSelectedPackages([]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePackageToggle = (pkg: Package) => {
    setSelectedPackages(prev => {
      const isSelected = prev.some(p => p.id === pkg.id);
      if (isSelected) {
        return prev.filter(p => p.id !== pkg.id);
      } else {
        return [...prev, pkg];
      }
    });
  };

  const handlePayment = async () => {
    if (selectedPackages.length === 0) {
      setError(t('checkIn.errors.selectPackage'));
      return;
    }

    if (!selectedPlayer) {
      setError(t('checkIn.errors.noPlayerSelected'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // First save the selected packages (like original checkout)
      const saveResponse = await fetch(`${apiUrl}/checkin/player/${selectedPlayer.id}/league/${selectedLeagueId}/save-changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          selectedPackageIds: selectedPackages.map(pkg => pkg.id)
        })
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        setError(errorData.error || t('checkIn.errors.saveFailed'));
        return;
      }

      // Then collect payment (like original checkout)
      const paymentResponse = await fetch(`${apiUrl}/checkin/player/${selectedPlayer.id}/league/${selectedLeagueId}/collect-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount: selectedTotal,
          paymentMethod,
          packageIds: selectedPackages.map(pkg => pkg.id)
        })
      });

      if (paymentResponse.ok) {
        const data = await paymentResponse.json();

        // Update the UI
        setPaidPackages(prev => [...prev, ...selectedPackages]);
        setAvailablePackages(prev => prev.filter(pkg => !selectedPackages.some(selected => selected.id === pkg.id)));

        // Show receipt with proper registration status from response
        setReceiptData({
          amount: selectedTotal,
          paymentMethod,
          packages: selectedPackages,
          registrationStatus: data.registrationStatus, // Use the actual response status
          timestamp: new Date().toLocaleString()
        });
        setShowReceiptModal(true);

        // Clear selected packages
        setSelectedPackages([]);

        // Update player status with the actual status from response
        if (data.registrationStatus) {
          setSelectedPlayer(prev => prev ? { ...prev, registrationStatus: data.registrationStatus } : null);
        }

        // Refresh player details to get updated data
        await fetchPlayerDetails(selectedPlayer.id, selectedLeagueId);

      } else {
        const errorData = await paymentResponse.json();
        setError(errorData.error || t('checkIn.errors.paymentFailed'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(t('checkIn.errors.paymentFailed'));
    } finally {
      setSaving(false);
    }
  };

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundPackageId, setRefundPackageId] = useState<number | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');
  const openRefundModal = (pkgId: number) => {
    setRefundPackageId(pkgId);
    setRefundError('');
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setRefundPackageId(null);
    setRefundError('');
  };

  const handleRefundPackage = async () => {
    if (!selectedPlayer || refundPackageId === null) return;
    setRefundLoading(true);
    setRefundError('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/refunds/refund-package`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          packageId: refundPackageId,
          playerId: selectedPlayer.id,
          bowlerId: selectedPlayer.bowlerId
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setRefundError(data.error || t('checkIn.errors.refundFailed'));
        return;
      }
      // Refresh player details and close modal
      await fetchPlayerDetails(selectedPlayer.id, selectedLeagueId);
      closeRefundModal();
      alert('Package refunded and squad entries removed successfully.');
    } catch (error) {
      setRefundError(t('checkIn.errors.refundFailed'));
    } finally {
      setRefundLoading(false);
    }
  };

  // Add Package Modal handlers
  const openAddPackageModal = async () => {
    setShowAddPackageModal(true);
    setPackageError('');
    setPackageLoading(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/checkin/league/${leagueId}/squads`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableSquads(data.squads || []);
      } else {
        setPackageError(t('checkIn.errors.loadSquadsFailed'));
      }
    } catch (error) {
      setPackageError(t('checkIn.errors.loadSquadsFailed'));
    } finally {
      setPackageLoading(false);
    }
  };

  const closeAddPackageModal = () => {
    setShowAddPackageModal(false);
    setNewPackage({
      name: '',
      description: '',
      price: 0,
      includes: []
    });
    setPackageError('');
  };

  const handleSquadToggle = (squadId: number) => {
    const isSelected = newPackage.includes.includes(squadId);
    
    if (isSelected) {
      setNewPackage({ 
        ...newPackage, 
        includes: newPackage.includes.filter(id => id !== squadId) 
      });
    } else {
      setNewPackage({ 
        ...newPackage, 
        includes: [...newPackage.includes, squadId] 
      });
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.name || !newPackage.description || !newPackage.price || newPackage.includes.length === 0) {
      setPackageError(t('checkIn.errors.fillAllFields'));
      return;
    }

    setPackageLoading(true);
    setPackageError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/checkin/league/${leagueId}/create-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newPackage.name,
          description: newPackage.description,
          price: newPackage.price,
          includes: JSON.stringify(newPackage.includes)
        })
      });

      if (response.ok) {
        alert(t('checkIn.addPackageModal.successMessage'));
        closeAddPackageModal();
        // Refresh available packages if player is selected
        if (selectedPlayer) {
          await fetchPlayerDetails(selectedPlayer.id, selectedLeagueId);
        }
      } else {
        const errorData = await response.json();
        setPackageError(errorData.error || t('checkIn.errors.createPackageFailed'));
      }
    } catch (error) {
      setPackageError(t('checkIn.errors.createPackageFailed'));
    } finally {
      setPackageLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-white hover:text-blue-200 transition duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>{t('checkIn.backButton')}</span>
              </button>
            </div>
            <div>
              <button
                onClick={openAddPackageModal}
                className="px-4 py-2 bg-purple-600/50 hover:bg-purple-700/50 text-white rounded-lg transition duration-200 flex items-center space-x-2 border border-purple-400/30"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>{t('checkIn.addPackageButton')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

       {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueCheckInHelp')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueCheckInHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Compact Search Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{t('checkIn.title')}</h2>
            {selectedPlayer && (
              <button
                onClick={handleNewSearch}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('checkIn.newSearch')}
              </button>
            )}
          </div>

          {!selectedPlayer && (
            <div className="flex space-x-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('checkIn.searchPlaceholder')}
                className="w-64 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{t('checkIn.searching')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>{t('checkIn.searchButton')}</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4">
              <div className="bg-red-500/20 border border-red-400 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="text-red-400 text-xl">⚠️</div>
                  <p className="text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && !selectedPlayer && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <h3 className="text-xl font-bold text-white mb-4">{t('checkIn.searchResults')}</h3>
            <div className="space-y-4">
              {searchResults.map((player) => (
                <div key={player.bowlerId} className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-md font-semibold text-white">{player.bowlerName}</h3>
                      <div className="text-sm text-blue-300 space-y-1">
                        <p>{t('checkIn.playerIdLabel')}: {player.bowlerId} | {t('checkIn.average')}: {player.average} | {t('checkIn.handicap')}: {player.handicap}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-3">
                    <button
                      onClick={() => handlePlayerSelect(player.playerId, leagueId)}
                      className="w-full bg-blue-600/20 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition duration-200 text-left border border-blue-600/30 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{t('checkIn.selectForCheckIn')}</div>
                      </div>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && hasSearched && searchResults.length === 0 && !error && !selectedPlayer && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-white mb-2">{t('checkIn.noPlayersFound')}</h3>
            <p className="text-blue-200">{t('checkIn.noPlayersMessage', { query: searchQuery })}</p>
          </div>
        )}

        {/* Pre-loaded bowler list — visible when no search results & no player selected */}
        {allBowlers.length > 0 && searchResults.length === 0 && !selectedPlayer && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">All Registered Bowlers</h3>
              <span className="text-blue-300 text-sm">{allBowlers.length} bowlers</span>
            </div>
            <div className="space-y-2">
              {allBowlers.slice(bowlerListPage * BOWLERS_PER_PAGE, (bowlerListPage + 1) * BOWLERS_PER_PAGE).map((b) => (
                <div key={b.bowlerId} className="bg-white/5 rounded-lg px-4 py-3 border border-white/10 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white font-semibold truncate">{b.bowlerName}</p>
                    <p className="text-blue-300 text-xs mt-0.5">
                      ID: {b.bowlerId} &nbsp;·&nbsp; Avg: {b.average} &nbsp;·&nbsp; Hdcp: {b.handicap}
                      {b.city ? ` · ${b.city}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      b.registrationStatus === 'active' ? 'bg-blue-600/20 text-blue-200 border-blue-500/30'
                      : b.registrationStatus === 'cancelled' ? 'bg-red-600/20 text-red-200 border-red-500/30'
                      : 'bg-yellow-600/20 text-yellow-200 border-yellow-500/30'
                    }`}>{b.registrationStatus}</span>
                    <button
                      onClick={() => handlePlayerSelect(b.playerId, leagueId)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition duration-200 whitespace-nowrap"
                    >
                      Select
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            {allBowlers.length > BOWLERS_PER_PAGE && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setBowlerListPage(p => Math.max(0, p - 1))}
                  disabled={bowlerListPage === 0}
                  className="px-3 py-1 rounded-lg text-sm text-white disabled:opacity-30 bg-white/10 hover:bg-white/20 disabled:cursor-not-allowed transition"
                >
                  ← Previous
                </button>
                <span className="text-blue-200 text-sm">
                  Page {bowlerListPage + 1} of {Math.ceil(allBowlers.length / BOWLERS_PER_PAGE)}
                </span>
                <button
                  onClick={() => setBowlerListPage(p => Math.min(Math.ceil(allBowlers.length / BOWLERS_PER_PAGE) - 1, p + 1))}
                  disabled={bowlerListPage >= Math.ceil(allBowlers.length / BOWLERS_PER_PAGE) - 1}
                  className="px-3 py-1 rounded-lg text-sm text-white disabled:opacity-30 bg-white/10 hover:bg-white/20 disabled:cursor-not-allowed transition"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Checkout Interface - Show when player is selected */}
        {selectedPlayer && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Check-In: {leagueName}
              </h2>

              <div className={`inline-block px-3 py-1 rounded-full text-sm mt-2 ${(() => {
                const status = selectedPlayer.registrationStatus || 'pending';
                switch (status) {
                  case 'cancelled':
                    return 'bg-red-600/20 text-red-200';
                  case 'pending':
                    return 'bg-yellow-600/20 text-yellow-200';
                  case 'active':
                    return 'bg-blue-600/20 text-blue-200';
                  case 'completed':
                    return 'bg-green-600/20 text-green-200';
                  default:
                    return 'bg-gray-600/20 text-gray-200';
                }
              })()
                }`}>
                Status: {selectedPlayer.registrationStatus || 'pending'}
              </div>
            </div>

            {checkoutLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                <span className="ml-3 text-white">{t('checkIn.loadingDetails')}</span>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Player Info Column */}
                <div
                  className="bg-white/5 rounded-lg p-4 cursor-pointer hover:bg-white/10 transition duration-200"
                  onClick={() => navigate(`/admin/bowlers/${selectedPlayer.bowlerId}`)}
                  title="Click to view bowler details"
                >
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <span>{t('checkIn.playerInfo')}</span>
                    <svg className="w-4 h-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-blue-300">
                      <span className="text-white font-medium">{t('checkIn.name')}:</span> {selectedPlayer.bowlerName} <span className="text-white font-medium">{t('checkIn.id')}:</span> {selectedPlayer.bowlerId}
                    </p>
                    <p className="text-blue-300">
                      <span className="text-white font-medium">{t('checkIn.average')}:</span> {selectedPlayer.average} <span className="text-white font-medium">{t('checkIn.handicap')}:</span> {selectedPlayer.handicap}
                    </p>
                    <p className="text-blue-300">
                      <span className="text-white font-medium">{t('checkIn.email')}:</span> {selectedPlayer.email}
                    </p>
                    <p className="text-blue-300">
                      <span className="text-white font-medium">{t('checkIn.location')}:</span> {selectedPlayer.city}, {selectedPlayer.country}
                    </p>

                  </div>
                </div>

                {/* Packages Bought Column */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">{t('checkIn.packagesBought')}</h3>
                  <div className="space-y-2">
                    {paidPackages.length > 0 ? (
                      paidPackages.map((pkg) => (
                        <div key={pkg.id} className="bg-green-600/20 border border-green-600/30 rounded-lg p-3">
                          <div className="text-white font-medium">{pkg.name}</div>
                          <div className="text-green-300 text-sm">${pkg.price}</div>
                          <button
                            onClick={() => openRefundModal(pkg.package_id)}
                            className="mt-2 px-3 py-1 bg-red-600/50 hover:bg-red-700/50 text-white rounded text-sm transition duration-200"
                          >
                            {t('checkIn.refundButton')}
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-blue-300 text-sm">{t('checkIn.noPackages')}</p>
                    )}
                  </div>
                </div>
                {/* Refund Modal */}
                {showRefundModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-xl font-bold mb-4 text-red-700">
                        {t('checkIn.refundModal.title')}
                      </h2>
                      <p className="mb-4 text-gray-700 text-sm">
                        {t('checkIn.refundModal.message')}
                      </p>
                      {refundError && (
                        <div className="mb-3 bg-red-100 text-red-700 rounded p-2 text-sm">{refundError}</div>
                      )}
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={closeRefundModal}
                          className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                          disabled={refundLoading}
                        >
                          {t('checkIn.refundModal.cancelButton')}
                        </button>
                        <button
                          onClick={handleRefundPackage}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold"
                          disabled={refundLoading}
                        >
                          {refundLoading ? t('checkIn.refundModal.processing') : t('checkIn.refundModal.confirmButton')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Available Packages Column */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">{t('checkIn.availablePackages')}</h3>
                  <div className="space-y-2">
                    {availablePackages.length > 0 ? (
                      availablePackages.map((pkg) => {
                        const isSelected = selectedPackages.some(selected => selected.id === pkg.id);
                        return (
                          <div
                            key={pkg.id}
                            className={`rounded-lg p-3 cursor-pointer transition duration-200 ${isSelected
                              ? 'bg-blue-600/30 border border-blue-500'
                              : 'bg-blue-600/20 border border-blue-600/30 hover:bg-blue-600/30'
                              }`}
                            onClick={() => handlePackageToggle(pkg)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-white/40'
                                }`}>
                                {isSelected && (
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="text-white font-medium">{pkg.name}</div>
                                <div className="text-blue-300 text-sm">${pkg.price}</div>
                                <div className="text-blue-400 text-xs mt-1">{pkg.description}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-blue-300 text-sm">No packages available</p>
                    )}
                  </div>
                </div>

                {/* Action Column */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Payment</h3>

                  {/* Cart Summary */}
                  <div className="mb-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <h4 className="text-white font-medium mb-2">{t('checkIn.selectedPackages', { count: selectedPackages.length })}</h4>
                      {selectedPackages.length > 0 ? (
                        <>
                          <div className="space-y-1 mb-3">
                            {selectedPackages.map((pkg) => (
                              <div key={pkg.id} className="flex justify-between text-sm">
                                <span className="text-blue-300 truncate">{pkg.name}</span>
                                <span className="text-white">${pkg.price}</span>
                              </div>
                            ))}
                          </div>
                          <div className="border-t border-white/20 pt-2">
                            <div className="flex justify-between font-semibold">
                              <span className="text-white">{t('checkIn.total')}:</span>
                              <span className="text-green-300">${selectedTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-blue-300 text-sm">No packages selected</p>
                      )}
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="mb-4">
                    <label className="block text-white font-medium mb-2">{t('checkIn.paymentMethod')}</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="cash" className="bg-gray-800 text-white">{t('checkIn.cash')}</option>
                      <option value="card" className="bg-gray-800 text-white">{t('checkIn.card')}</option>
                      <option value="check" className="bg-gray-800 text-white">{t('checkIn.check')}</option>
                      <option value="other" className="bg-gray-800 text-white">{t('checkIn.other')}</option>
                    </select>
                  </div>

                  {/* Payment Button */}
                  <button
                    onClick={handlePayment}
                    disabled={selectedPackages.length === 0 || saving}
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center space-x-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{t('checkIn.processing')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{t('checkIn.completePayment')} (${selectedTotal.toFixed(2)})</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
            {/* Receipt Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
              <div className="text-center">
                <div className="text-3xl mb-2">🎳</div>
                <h2 className="text-xl font-bold">{t('checkIn.receiptModal.title')}</h2>
                <p className="text-blue-100 text-sm">{leagueName || 'Bowling League'}</p>
              </div>
            </div>

            {/* Receipt Body */}
            <div className="p-6 text-gray-800">
              {/* Player Info */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-2">{t('checkIn.playerInfo')}</h3>
                <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                  <p><span className="font-medium">{t('checkIn.name')}:</span> {selectedPlayer?.bowlerName}</p>
                  <p><span className="font-medium">{t('checkIn.id')}:</span> {selectedPlayer?.bowlerId}</p>
                  <p><span className="font-medium">{t('checkIn.email')}:</span> {selectedPlayer?.email}</p>
                </div>
              </div>

              {/* Package Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-2">{t('checkIn.receiptModal.packages')}</h3>
                <div className="space-y-2">
                  {receiptData.packages.map((pkg, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div>
                        <p className="font-medium">{pkg.name}</p>
                        <p className="text-sm text-gray-600">{pkg.description}</p>
                      </div>
                      <span className="font-semibold">${parseFloat(pkg.price.toString()).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-2">{t('checkIn.receiptModal.paymentSuccessful')}</h3>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span>{t('checkIn.receiptModal.amount')}:</span>
                    <span className="font-semibold">${receiptData.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('checkIn.receiptModal.method')}:</span>
                    <span className="font-semibold capitalize">{receiptData.paymentMethod}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('checkIn.receiptModal.status')}:</span>
                    <span className={`font-semibold capitalize ${receiptData.registrationStatus === 'completed' ? 'text-green-600' : 'text-blue-600'
                      }`}>
                      {receiptData.registrationStatus}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{t('checkIn.receiptModal.timestamp')}:</span>
                    <span>{receiptData.timestamp}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Receipt Actions */}
            <div className="flex space-x-3 p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => window.print()}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print</span>
              </button>
              <button
                onClick={() => {
                  setShowReceiptModal(false);
                  // Stay in the checkout dashboard, don't navigate away
                }}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{t('checkIn.receiptModal.closeButton')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Package Modal */}
      {showAddPackageModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-8 border border-purple-400 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{t('checkIn.addPackageModal.title')}</h2>
              <button
                onClick={closeAddPackageModal}
                className="text-purple-200 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error Display */}
            {packageError && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
                {packageError}
              </div>
            )}

            {/* Package Details Form */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-purple-200 font-semibold mb-2">
                  {t('checkIn.addPackageModal.name')} *
                </label>
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  className="w-full px-4 py-2 bg-purple-800/50 border border-purple-400 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('checkIn.addPackageModal.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-purple-200 font-semibold mb-2">
                  {t('checkIn.addPackageModal.description')} *
                </label>
                <textarea
                  value={newPackage.description}
                  onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-purple-800/50 border border-purple-400 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('checkIn.addPackageModal.descriptionPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-purple-200 font-semibold mb-2">
                  {t('checkIn.addPackageModal.price')} *
                </label>
                <input
                  type="number"
                  value={newPackage.price || ''}
                  onChange={(e) => setNewPackage({ ...newPackage, price: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 bg-purple-800/50 border border-purple-400 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Squad Selection */}
            <div className="mb-6">
              <label className="block text-purple-200 font-semibold mb-3">
                {t('checkIn.addPackageModal.includesSquads')} *
              </label>
              
              {packageLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  <span className="ml-3 text-purple-200">{t('checkIn.addPackageModal.loadingSquads')}</span>
                </div>
              ) : availableSquads.length === 0 ? (
                <div className="text-center py-8 text-purple-200">
                  {t('checkIn.addPackageModal.selectSquads')}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableSquads.map((squad) => (
                    <div
                      key={squad.id}
                      onClick={() => handleSquadToggle(squad.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        newPackage.includes.includes(squad.id)
                          ? 'bg-purple-600 border-purple-400 shadow-lg shadow-purple-500/50'
                          : 'bg-purple-800/30 border-purple-600 hover:border-purple-400'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-white mb-1">
                            {squad.name}
                          </div>
                          <div className="text-sm text-purple-200">
                            {new Date(squad.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          <div className="text-sm text-purple-300">
                            {squad.start_time} - {squad.end_time}
                          </div>
                          <div className="text-xs text-purple-400 mt-1">
                            {squad.games_per_squad} games
                          </div>
                        </div>
                        <div className={`flex-shrink-0 ml-3 ${
                          newPackage.includes.includes(squad.id) ? 'text-white' : 'text-purple-400'
                        }`}>
                          {newPackage.includes.includes(squad.id) ? (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" strokeWidth="2" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4 border-t border-purple-600">
              <button
                onClick={closeAddPackageModal}
                className="flex-1 px-6 py-3 bg-purple-700 hover:bg-purple-600 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('checkIn.addPackageModal.cancelButton')}
              </button>
              <button
                onClick={handleCreatePackage}
                disabled={packageLoading}
                className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {packageLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('checkIn.addPackageModal.creating')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('checkIn.addPackageModal.createButton')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInComponent;